<template>
  <div class="subscription-plans-dark">
    <div class="plans-header">
      <h2>💎 {{ $t('plans.title') }}</h2>
      <p>{{ $t('plans.description') }}</p>
    </div>
    
    <div class="billing-container">
      <div class="billing-toggle">
        <span>{{ $t('plans.montlyFactor') }}</span>
        <label class="toggle-switch">
          <input type="checkbox" v-model="annualBilling">
          <span class="slider round"></span>
        </label>
        <span>{{ $t('plans.yearlyFactor') }} <span class="discount-badge">-20%</span></span>
      </div>
      
      <router-link to="/dashboard" class="open-studio-button">
        {{ $t('plans.openStudio') }}
      </router-link>
    </div>
    
    <div class="plans-container">
      <div class="plan" :class="{ 
        'free-plan': plan.type === 'free', 
        'premium-plan': plan.type === 'pro', 
        'enterprise-plan': plan.type === 'enterprise',
        'highlighted': plan.highlighted 
      }" v-for="plan in computedPlans" :key="plan.type">
        <div class="plan-badge" v-if="plan.recommended">{{ $t('plans.pro.popular') }}</div>
        <div class="plan-header">
          <h3>{{ $t(`plans.${plan.type}.title`) }}</h3>
          <div class="price-container">
            <div class="price">{{ plan.price }}</div>
            <div class="billing" v-if="plan.type !== 'free'">
              {{ annualBilling ? $t('plans.yearlyFactor') : $t('plans.montlyFactor') }}
            </div>
          </div>
          <div class="annual-savings" v-if="plan.savings && annualBilling">
            {{ $t('plans.pro.saving') }} {{ plan.savings }}
          </div>
        </div>
        
        <ul class="features-list">
          <li v-for="(feature, index) in getPlanFeatures(plan.type)" :key="index">
            <span class="check-icon">✓</span> {{ feature }}
          </li>
        </ul>
        
        <button 
          @click="selectPlan(plan)" 
          :class="['plan-button', 
                  { 'current-plan': plan.type === currentPlan,
                    'premium-button': plan.type === 'pro',
                    'enterprise-button': plan.type === 'enterprise' }]"
        >
          {{ plan.type === currentPlan ? $t('plans.free.button') : $t(`plans.${plan.type}.button`) }}
        </button>
        
        <div class="plan-footer" v-if="plan.type !== 'free'">
          <span class="trial-text">{{ $t(`plans.${plan.type}.testDesc`) }}</span>
        </div>
      </div>
    </div>
    
    <div class="payment-methods">
      <h3>{{ $t('plans.availablePayments.title') }}</h3>
      <div class="payment-icons">
        <span class="payment-icon">💳 {{ $t('plans.availablePayments.creditCard') }}</span>
        <span class="payment-icon">📱 {{ $t('plans.availablePayments.paypal') }}</span>
        <span class="payment-icon">👛 {{ $t('plans.availablePayments.stripe') }}</span>
      </div>
      <p class="security-note">{{ $t('plans.availablePayments.secDesc') }}</p>
    </div>
    
    <div class="faq-section">
      <h3>{{ $t('plans.faq.title') }}</h3>
      <div class="faq-list">
        <div class="faq-item">
          <div class="faq-question">{{ $t('plans.faq.q1') }}</div>
          <div class="faq-answer">{{ $t('plans.faq.a1') }}</div>
        </div>
        <div class="faq-item">
          <div class="faq-question">{{ $t('plans.faq.q2') }}</div>
          <div class="faq-answer">{{ $t('plans.faq.a2') }}</div>
        </div>
        <div class="faq-item">
          <div class="faq-question">{{ $t('plans.faq.q3') }}</div>
          <div class="faq-answer">{{ $t('plans.faq.a3') }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SubscriptionPlansDark',
  data() {
    return {
      annualBilling: false,
      currentPlan: 'free',
      plans: [
        {
          type: 'free',
          monthlyPrice: '$0',
          annualPrice: '$0',
          recommended: false,
          highlighted: false,
        },
        {
          type: 'pro',
          monthlyPrice: '$5.99',
          annualPrice: '$4.79',
          recommended: true,
          highlighted: true,
          savings: '$14.40'
        },
        {
          type: 'enterprise',
          monthlyPrice: '$19.99',
          annualPrice: '$15.99',
          recommended: false,
          savings: '$48.00'
        }
      ],
      // Definimos las características directamente aquí como arrays
      planFeatures: {
        free: [
          this.$t('plans.free.features.0'),
          this.$t('plans.free.features.1'),
          this.$t('plans.free.features.2'),
          this.$t('plans.free.features.3'),
          this.$t('plans.free.features.4')
        ],
        pro: [
          this.$t('plans.pro.features.0'),
          this.$t('plans.pro.features.1'),
          this.$t('plans.pro.features.2'),
          this.$t('plans.pro.features.3'),
          this.$t('plans.pro.features.4'),
          this.$t('plans.pro.features.5'),
          this.$t('plans.pro.features.6')
        ],
        enterprise: [
          this.$t('plans.enterprise.features.0'),
          this.$t('plans.enterprise.features.1'),
          this.$t('plans.enterprise.features.2'),
          this.$t('plans.enterprise.features.3'),
          this.$t('plans.enterprise.features.4'),
          this.$t('plans.enterprise.features.5'),
          this.$t('plans.enterprise.features.6'),
          this.$t('plans.enterprise.features.7'),
          this.$t('plans.enterprise.features.8')
        ]
      }
    }
  },
  computed: {
    computedPlans() {
      return this.plans.map(plan => {
        const price = this.annualBilling ? plan.annualPrice : plan.monthlyPrice;
        
        return {
          ...plan,
          name: this.$t(`plans.${plan.type}.title`),
          price,
          cta: this.$t(`plans.${plan.type}.button`)
        }
      })
    }
  },
  methods: {
    selectPlan(plan) {
      if (plan.type === this.currentPlan) {
        return
      }
      alert(`Redirigiendo a proceso de ${plan.type === 'free' ? 'activación' : 'pago'} para plan ${plan.name}`)
    },
    getPlanFeatures(planType) {
      // Obtenemos las características del objeto local
      return this.planFeatures[planType] || [];
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables.scss';
/* Tus estilos existentes se mantienen igual */
.subscription-plans-dark {
  max-width: 1200px;
  margin: 0 auto;
  padding: 30px 20px;
  background-color: $dark-bg-primary;
  color: $dark-text-primary;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

.plans-header {
  text-align: center;
  margin-bottom: 40px;
}

.plans-header h2 {
  font-size: 2.2rem;
  margin-bottom: 15px;
  color: $dark-accent-primary;
  font-weight: 700;
}

.plans-header p {
  font-size: 1.1rem;
  color: $dark-text-secondary;
  max-width: 600px;
  margin: 0 auto;
}

.billing-toggle {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 15px;
  margin-bottom: 40px;
  padding: 15px;
  background-color: $dark-bg-secondary;
  border-radius: $border-radius-button;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
  border: 1px solid $dark-border-color;
}

.billing-toggle span {
  font-weight: 600;
  color: $dark-text-primary;
}

.discount-badge {
  background-color: $dark-accent-secondary;
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.8rem;
  margin-left: 5px;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 60px;
  height: 30px;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-switch .slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: $dark-bg-tertiary;
  transition: .4s;
}

.toggle-switch .slider:before {
  position: absolute;
  content: "";
  height: 22px;
  width: 22px;
  left: 4px;
  bottom: 4px;
  background-color: white;
  transition: .4s;
}

.toggle-switch input:checked + .slider {
  background-color: $dark-accent-primary;
}

.toggle-switch input:checked + .slider:before {
  transform: translateX(30px);
}

.toggle-switch .slider.round {
  border-radius: 34px;
}

.toggle-switch .slider.round:before {
  border-radius: 50%;
}

.plans-container {
  display: flex;
  flex-wrap: wrap;
  gap: 25px;
  justify-content: center;
  margin-bottom: 50px;
}

.plan {
  flex: 1;
  min-width: 280px;
  max-width: 350px;
  padding: 0;
  border-radius: $border-radius-card;
  text-align: center;
  transition: all 0.3s ease;
  background: $dark-bg-secondary;
  border: 1px solid $dark-border-color;
  position: relative;
  overflow: hidden;
  box-shadow: $dark-card-shadow;
}

.plan:hover {
  transform: translateY(-10px);
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.2);
}

.plan-badge {
  position: absolute;
  top: 15px;
  right: -30px;
  background: $dark-accent-primary;
  color: white;
  padding: 5px 30px;
  font-size: 0.8rem;
  font-weight: bold;
  transform: rotate(45deg);
  width: 120px;
}

.free-plan {
  border-top: 4px solid $dark-text-secondary;
}

.premium-plan {
  border-top: 4px solid $dark-accent-primary;
}

.enterprise-plan {
  border-top: 4px solid $dark-accent-secondary;
}

.highlighted {
  transform: scale(1.05);
  border: 2px solid $dark-accent-primary;
  box-shadow: 0 0 25px rgba(187, 134, 252, 0.3);
}

.highlighted:hover {
  transform: scale(1.05) translateY(-10px);
}

.plan-header {
  padding: 30px 20px 20px;
  border-bottom: 1px solid $dark-border-color;
}

.plan h3 {
  margin: 0 0 15px 0;
  font-size: 1.5rem;
  font-weight: 700;
}

.free-plan h3 {
  color: $dark-text-secondary;
}

.premium-plan h3 {
  color: $dark-accent-primary;
}

.enterprise-plan h3 {
  color: $dark-accent-secondary;
}

.price-container {
  margin-bottom: 10px;
}

.price {
  font-size: 2.5rem;
  font-weight: 800;
  margin: 10px 0;
}

.free-plan .price {
  color: $dark-text-secondary;
}

.premium-plan .price {
  color: $dark-accent-primary;
}

.enterprise-plan .price {
  color: $dark-accent-secondary;
}

.billing {
  font-size: 0.9rem;
  color: $dark-text-secondary;
}

.annual-savings {
  background-color: rgba(3, 218, 198, 0.1);
  color: $dark-accent-secondary;
  padding: 5px 10px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  display: inline-block;
  margin-top: 10px;
}

.features-list {
  list-style-type: none;
  padding: 20px;
  margin: 0;
  text-align: left;
}

.features-list li {
  padding: 12px 0;
  border-bottom: 1px solid $dark-border-color;
  color: $dark-text-primary;
  display: flex;
  align-items: center;
}

.features-list li:last-child {
  border-bottom: none;
}

.check-icon {
  background-color: $dark-accent-primary;
  color: white;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  font-size: 0.8rem;
  flex-shrink: 0;
}

.plan-button {
  width: calc(100% - 40px);
  margin: 20px;
  padding: 15px;
  border: none;
  border-radius: $border-radius-button;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 700;
  transition: all 0.3s ease;
}

.free-plan .plan-button {
  background-color: $dark-bg-tertiary;
  color: $dark-text-primary;
}

.premium-button {
  background: linear-gradient(to right, $dark-accent-primary, #a29bfe);
  color: white;
}

.enterprise-button {
  background: linear-gradient(to right, $dark-accent-secondary), #00dac6;
  color: white;
}

.plan-button:hover:not(.current-plan) {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
}

.premium-button:hover:not(.current-plan) {
  box-shadow: 0 5px 15px rgba(187, 134, 252, 0.4);
}

.enterprise-button:hover:not(.current-plan) {
  box-shadow: 0 5px 15px rgba(3, 218, 198, 0.4);
}

.current-plan {
  background-color: $dark-bg-tertiary;
  color: $dark-text-secondary;
  cursor: not-allowed;
}

.plan-footer {
  padding: 0 20px 20px;
}

.trial-text {
  font-size: 0.9rem;
  color: $dark-text-secondary;
  font-style: italic;
}

.payment-methods {
  text-align: center;
  margin-bottom: 50px;
  padding: 30px;
  background-color: $dark-bg-secondary;
  border-radius: $border-radius-card;
  border: 1px solid $dark-border-color;
}

.payment-methods h3 {
  margin-bottom: 20px;
  color: $dark-text-primary;
}

.payment-icons {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 15px;
  flex-wrap: wrap;
}

.payment-icon {
  font-size: 1.2rem;
  padding: 8px 15px;
  background-color: $dark-bg-tertiary;
  border-radius: 8px;
}

.security-note {
  font-size: 0.9rem;
  color: $dark-text-secondary;
}

.faq-section {
  margin-bottom: 30px;
}

.faq-section h3 {
  text-align: center;
  margin-bottom: 30px;
  color: $dark-text-primary;
}

.faq-list {
  max-width: 800px;
  margin: 0 auto;
}

.faq-item {
  margin-bottom: 15px;
  border: 1px solid $dark-border-color;
  border-radius: 12px;
  overflow: hidden;
}

.faq-question {
  padding: 15px 20px;
  background-color: $dark-bg-secondary;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.faq-question::after {
  content: "+";
  font-size: 1.2rem;
}

.faq-answer {
  padding: 15px 20px;
  background-color: $dark-bg-tertiary;
  color: $dark-text-secondary;
}

@media (max-width: 968px) {
  .plans-container {
    flex-direction: column;
    align-items: center;
  }
  
  .plan {
    max-width: 100%;
  }
  
  .highlighted {
    transform: scale(1);
  }
  
  .highlighted:hover {
    transform: translateY(-10px);
  }
}

@media (max-width: 768px) {
  .plans-header h2 {
    font-size: 1.8rem;
  }
  
  .billing-toggle {
    flex-direction: column;
    gap: 10px;
    border-radius: $border-radius-button;
  }
  
  .price {
    font-size: 2rem;
  }
  
  .payment-icons {
    flex-direction: column;
    align-items: center;
  }
}

.billing-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 40px;
  flex-wrap: wrap;
  gap: 20px;
}

.billing-toggle {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 15px;
  padding: 15px;
  background-color: $dark-bg-secondary;
  border-radius: $border-radius-button;
  border: 1px solid $dark-border-color;
  flex-grow: 1;
  max-width: 400px;
}

.open-studio-button {
  padding: 12px 24px;
  background: linear-gradient(to right, $dark-accent-primary, #176d2a);
  color: white;
  border: none;
  border-radius: $border-radius-button;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;
  white-space: nowrap;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(134, 252, 134, 0.4);
  }
}

/* Ajustes responsivos */
@media (max-width: 768px) {
  .billing-container {
    flex-direction: column;
    align-items: stretch;
  }
  
  .billing-toggle {
    max-width: 100%;
  }
  
  .open-studio-button {
    align-self: center;
  }
}
</style>